import { useForm } from 'react-hook-form';
import { ReactNode } from 'react';
import { E164Number } from 'libphonenumber-js';
import getCountryCode from 'utils/getCountryCode';
import useFormContactSMESchema, {
  FormContactSMESchema,
} from 'components/SMEContactForm/hooks/useFormContactSMESchema';
import { useSelector } from 'react-redux';
import { AddressDetailsSelector, AddressSelector } from 'store/selectors';
import { zodResolver } from '@hookform/resolvers/zod';
import FormInputItem from 'ui/molecules/FormInputItem';
import { useTranslation } from 'react-i18next';
import { Box, Text } from '@chakra-ui/react';
import FormPhoneItem from 'ui/molecules/FormPhoneItem';

type Props = {
  submitElement: ReactNode;
  onSubmit: (data: FormContactSMESchema) => Promise<void>;
  isSubmitting: boolean;
};

export const SMEContactForm = ({ onSubmit, ...props }: Props) => {
  const { t } = useTranslation();
  const address = useSelector(AddressSelector);
  const addressDetails = useSelector(AddressDetailsSelector);
  const formSchema = useFormContactSMESchema();
  const { register, handleSubmit, formState, control, setValue } =
    useForm<FormContactSMESchema>({
      resolver: zodResolver(formSchema),
      shouldFocusError: true,
    });
  const { errors } = formState;

  // --------------------- render ------------------------

  return (
    <form
      style={
        props.isSubmitting
          ? {
              pointerEvents: 'none',
              opacity: '0.9',
              userSelect: 'none',
            }
          : {}
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <FormInputItem
        variant="primaryOutline"
        label={t('form.contact.sme.company')}
        errorMessage={errors.company?.message}
        {...register('company', {
          required: false,
        })}
      />

      <Box padding="10px 0px" w="full">
        <Text>{t('form.contact.sme.address')}</Text>
        <Text fontWeight="bold">{address}</Text>
      </Box>

      <FormInputItem
        variant="primaryOutline"
        label={t('form.contact.sme.tva')}
        errorMessage={errors.tva?.message}
        {...register('tva', {
          required: false,
        })}
      />

      <FormInputItem
        variant="primaryOutline"
        label={t('form.contact.sme.firstName')}
        errorMessage={errors.firstName?.message}
        {...register('firstName', {
          required: true,
        })}
        required
      />

      <FormInputItem
        variant="primaryOutline"
        label={t('form.contact.sme.lastName')}
        errorMessage={errors.lastName?.message}
        {...register('lastName', {
          required: true,
        })}
        required
      />

      <FormInputItem
        variant="primaryOutline"
        label={t('form.contact.sme.email')}
        errorMessage={errors.email?.message}
        {...register('email', {
          required: true,
        })}
        required
      />

      <FormPhoneItem
        label={t('form.contact.sme.phone')}
        errorMessage={errors.phone?.message}
        formControl={control}
        onChangeCallback={(value: E164Number) => {
          setValue('phone', value, { shouldValidate: true });
        }}
        defaultCountry={getCountryCode(addressDetails.countryCode || 'BE')}
        {...register('phone', {
          required: true,
        })}
        required
      />

      <Text py={4} color="primaryColor">
        {t('form.mandatory.label')}
      </Text>

      {props.submitElement}
    </form>
  );
};
export default SMEContactForm;
